import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Box, Card, CardActions, CardContent, Container, Typography, Alert, Stack, CircularProgress, Grid2, Divider } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { initializePaddle } from '@paddle/paddle-js';

const transactionId = new URLSearchParams(window.location.search).get('_ptxn');

const formatMoney = (amount, currency) => {
  if (amount == null || !currency) return null;
  try {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency,
    }).format(amount / 100);
  } catch {
    return `${amount / 100} ${currency}`;
  }
};

function Row({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography fontWeight={bold ? 700 : 400}>{label}</Typography>
      <Typography fontWeight={bold ? 700 : 400}>{value}</Typography>
    </Stack>
  );
}

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get('_ptxn');
  const paddleRef = useRef(null);
  const [error, setError] = useState(null);
  const [state, setState] = useState('loading'); // loading | closed | completed
  const [summary, setSummary] = useState(null);
  const [liveTotals, setLiveTotals] = useState(null);   // from checkout events, major units → normalized below

  const totals = liveTotals ?? (summary && {
    subtotal: summary.subtotal, tax: summary.tax, total: summary.total, currency: summary.currency,
  });  
  // const retryPurchase = () => {
  //   if (!transactionId) { setError('Missing transaction'); return; }
  //   paddleRef.current.Checkout.open({ transactionId }); //{ transactionId }
  // }

  const displayPrice = useMemo(() => {
    if (!summary?.currency) return null;
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: summary.currency,
      }).format(summary.amount / 100);
    } catch {
      return `${summary.amount / 100} ${summary.currency}`;
    }
  }, [summary]);

  useEffect(() => {
    if (!transactionId) return;
    fetch(`/api/paddle/txn/${transactionId}/summary`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setSummary)
      .catch((error) => {
        console.log('fetch-error', error);
      });                       // page still works without the summary
  }, [transactionId]);  

  useEffect(() => {
    setError(null);
    fetch('/api/paddle/config')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(({ environment, token }) => initializePaddle({
        environment,
        token,
        eventCallback: (event) => {
          console.log(`event: ${event.name}`, event);
          if (event.data?.totals) {
            setLiveTotals({
              subtotal: Math.round(event.data.totals.subtotal * 100),  // events: major units
              tax: Math.round(event.data.totals.tax * 100),
              total: Math.round(event.data.totals.total * 100),
              currency: event.data.currency_code,
            });
          }
          if (event.name === 'checkout.completed') setState('completed');
          if (event.name === 'checkout.closed' && state !== 'completed') setState('closed');
        },
      })).then((paddle) => {
       // _ptxn in the URL is picked up automatically on init
      console.log('paddle', paddle);
      paddleRef.current = paddle;
      paddle.Checkout.open({
        transactionId,                    // manual open — inline doesn't auto-open from _ptxn
        customerAuthToken: searchParams.get('ctk'),
        settings: {
          displayMode: 'inline',
          variant: 'one-page',
          frameTarget: 'paddle-checkout',        // class name of the container
          frameInitialHeight: 450,
          frameStyle: 'width: 100%; background-color: transparent; border: none;',
          showAddDiscounts: false,
          theme: 'dark',
        },
      });
      
    }).catch(() => setError('Unable to load checkout'));
  }, []);

  return (
    <Container sx={{ mt: 5 }} maxWidth="lg">
      <Grid2 container={true} spacing={{ xs: 2, sm: 10 }}>
        <Grid2
          size={{ xs: 12, md: 4 }}
          sx={{
            pt: { xs: 0, md: 4 },
            pl: { xs: 2, sm: 3 },
            pr: { xs: 2, sm: 0 },
          }}
        >
          {summary ? (
            <Stack spacing={2}>
              
              <Grid2 container={true} spacing={2}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <img src={summary.posterUrl} alt={summary.title || "Course"} style={{ width: '100%', maxWidth: 120, borderRadius: 6 }} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 8 }}>
                  <Typography variant="h6">{summary.title}</Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    title={summary.description}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1, // Change this number to limit to your desired number of lines
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {summary.description.length > 150 ? summary.description.slice(0, 150) + '\u2026' : summary.description}
                  </Typography>
                </Grid2>
              </Grid2>

              {totals && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Row label="Course" value={formatMoney(totals.subtotal, totals.currency)} />
                  <Row label="Estimated Tax" value={formatMoney(totals.tax, totals.currency)} />
                  <Divider />
                  <Row label="Total" bold value={formatMoney(totals.total, totals.currency)} />
                  {/* <Row label="Grand Total" bold value={`${displayPrice}`} /> */}
                </Stack>
              )}                

            </Stack>
          ) : null}

        </Grid2>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Card sx={{ p: 0 }}>
            <CardContent>
              <div className="paddle-checkout"></div>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
      {/* {state === 'loading' ? (
        <Stack direction="column" sx={{ alignItems: 'center' }} spacing={4}>
          <CircularProgress variant="indeterminate" />
          <Typography>Return to the app once your purchase is complete.</Typography>
        </Stack>
      ) : null}
      {state === 'closed' ? (
        <Stack direction="column" spacing={4}>
          <Typography>Checkout was canceled. You can close this window and return to the OpenGolfSim app.</Typography>
          <Button
            fullWidth={true}
            variant="contained"
            color="secondary"
            onClick={retryPurchase}
          >
            Retry Purchase
          </Button>
        </Stack>
      ) : null} */}
    </Container>
  );
}